import React, { useCallback } from 'react';
import clsx from 'clsx';
import {
    DialogContent, DialogTitle, TextField,
} from '@material-ui/core';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';

import * as formHelpers from './formHelpers';
import { shouldEnableProximityPlacement } from './formHelpers';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import BasicInformationSection from '../components/BasicInformationSection.temporary';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration.temporary';
import StorageSettingsSection from '../components/StorageSettingsSection.temporary';
import NetworkSettingsSection from '../components/NetworkSettingsSection.temporary';
import DescriptionSection from '../components/DescriptionSection.temporary';
import CredentialSelector from '../components/CredentialSelector.temporary';
import RegionSelector from '../components/RegionSelector.temporary';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import CapacityReservationsSection from '../components/CapacityReservationsSection.temporary';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';

const AzureCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, volSizeLimits,
        availability, quotas, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({ provider: 'azure', props, formHelpers });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, regionAvailable, regionMissingInstances,
    } = availability;

    // Azure: update proximity placement when head node instance changes
    const handleServerInstanceChange = useCallback((instanceId) => {
        const updatedForm = { ...form, serverInstanceId: instanceId };
        setField({
            serverInstanceId: instanceId,
            useProximityPlacement: shouldEnableProximityPlacement(updatedForm),
            ...(!instanceId.includes('Flex') ? { serverOcpus: 1, serverMem: 1 } : {}),
        });
    }, [form, setField]);

    // Compute node instance change with proximity placement update
    const handleComputeInstanceChange = useCallback((index, instanceId) => {
        const updatedGroups = [...(form?.nodeGroups || [])];
        updatedGroups[index] = { ...updatedGroups[index], instance_id: instanceId };
        const updatedForm = { ...form, nodeGroups: updatedGroups };
        setField({
            nodeGroups: updatedGroups,
            useProximityPlacement: shouldEnableProximityPlacement(updatedForm),
        });
    }, [form, setField]);

    // Azure compute node render prop
    const renderAzureGroupFields = useCallback((group, index, { updateGroup }) => (
        <ComputeNodeGroupFields
            group={group} index={index} updateGroup={updateGroup}
            provider="azure" imageMode={form?.imageMode} maxCount={250}
            onInstanceChange={handleComputeInstanceChange}
            allowedInstanceIds={marketplace.allowedComputeIds}
            disableStackFields={marketplace.disableStackFields}
            disabled={disableInputs}
        />
    ), [form?.imageMode, marketplace, handleComputeInstanceChange, disableInputs]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="azure" providerLabel="Azure" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">

                                <BasicInformationSection
                                    form={form} setField={setField} provider="azure"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                />

                                {/* Infrastructure + Resource Manager (two columns) */}
                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard
                                            icon="cloud" title="Infrastructure" fillHeight
                                            action={form.regionId ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={regionAvailable}
                                                    missingInstances={regionMissingInstances} error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="azure" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <InstanceSizeSelector provider="azure" value={form.serverInstanceId || ''}
                                                    onChange={handleServerInstanceChange}
                                                    allowedInstanceIds={marketplace.config?.instance_type}
                                                    label="Head Node Size" />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <RegionSelector provider="azure" value={form.regionId || ''}
                                                    onChange={(id) => setField('regionId', id)}
                                                    allowedRegionIds={marketplace.config?.allowed_regions?.length > 0
                                                        ? marketplace.config.allowed_regions : undefined}
                                                />
                                                <TextField
                                                    className={clsx(classes.input)}
                                                    style={{ flex: 1 }}
                                                    label="Image Resource Group Name"
                                                    variant="outlined"
                                                    value={form.imageResourceGroupName || ''}
                                                    onChange={(e) => setField('imageResourceGroupName', e.target.value)}
                                                    placeholder="Exp: Image_group_1"
                                                />
                                            </div>
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        <ResourceManagerConfiguration
                                            form={form} setField={setField}
                                            hideBursting={marketplace.isMarketStack}
                                            lockManager={Boolean(marketplace.config?.resource_manager)}
                                        />
                                    </div>
                                </div>

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="azure" advanced={advanced}
                                    marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {/* Advanced: Network + Capacity Reservations */}
                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        <div style={columnStyle}>
                                            <NetworkSettingsSection
                                                form={form} setField={setField} fillHeight
                                            />
                                        </div>
                                        <div style={columnStyle}>
                                            <CapacityReservationsSection
                                                form={form} setField={setField}
                                                fillHeight showAutoExpire
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Advanced: Storage Settings */}
                                {advanced && (
                                    <StorageSettingsSection
                                        form={form} setField={setField} provider="azure"
                                        volSizeLimits={volSizeLimits}
                                    />
                                )}

                                {/* Compute Nodes */}
                                <ComputeNodesSection
                                    form={form} setField={setField}
                                    disabled={disableInputs}
                                    maxGroups={250}
                                    tooltip="Regions, sizes and quotas vary by account."
                                    renderGroupFields={renderAzureGroupFields}
                                />

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default AzureCluster;
